import React, { useEffect, useState } from "react";
import Icon from "../../assets/icons";
import useRequest from "../../components/hooks/use-request";
import Pagination from "../../components/pagination/pagination";
import NotificationModal from "../notification/notification-modal";
import Tabs from "../../components/tab";
import PrivilegesTable from "./privileges/privileges-table";
import RolesTable from "./roles/roles-table";
import CreatePrivilege from "./privileges/create-privilege";
import CreateRole from "./roles/create-role";

const Roles = () => {
  const [activeTab, setActiveTab] = useState<"roles" | "privileges">("roles");
  const [modalVisible, setModalVisible] = useState(false);
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const params = new URLSearchParams(new URL(window.location.href).search);
  const [currentPage, setCurrentPage] = useState(
    Number(params.get("page") || 1)
  );
  const [totalPages, setTotalPages] = useState<number>(1);
  const itemsPerPage = 10;
  const [privileges, setPrivileges] = useState<any>();
  const [roles, setRoles] = useState<any>();
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const userToken = localStorage.getItem("token");
  const { makeRequest: getRoles } = useRequest(`/roles`, "GET");
  const { makeRequest: getPrivileges } = useRequest("/privileges", "GET", {
    Authorization: `Bearer ${userToken}`,
  });
  const [modalNotifyVisible, setModalNotifyVisible] = useState(false);

  const updateUrlParams = (params: { [key: string]: string | number }) => {
    const url = new URL(window.location.href);
    Object.keys(params).forEach((key) => {
      url.searchParams.set(key, params[key].toString());
    });
    window.history.pushState({}, "", url.toString());
  };

  useEffect(() => {
    const storedSearchQuery = params.get("search") || "";
    const storedStatus = params.get("status") || "";
    const storedUserType = params.get("type") || activeTab;

    setSearchQuery(storedSearchQuery);
    setSelectedStatus(storedStatus);
    setActiveTab(storedUserType as "roles" | "privileges");
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    updateUrlParams({
      page: currentPage,
      search: searchQuery,
      status: selectedStatus,
      type: activeTab,
    });
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchQuery, selectedStatus, currentPage]);

  const fetchData = async () => {
    const page = currentPage;
    const limit = itemsPerPage;
    const paramsRequests: {
      limit: number;
      page: number;
      status?: string;
      title?: string;
      caseID?: string;
      email?: string;
    } = {
      limit,
      page,
    };
    const paramsMediators: {
      limit: number;
      page: number;
      status?: string;
      fullName?: string;
      email?: string;
    } = {
      limit,
      page,
    };

    if (selectedStatus) {
      paramsRequests.status = selectedStatus;
      paramsMediators.status = selectedStatus;
    }

    if (searchQuery) {
      if (
        searchQuery.startsWith("#") &&
        !isNaN(Number(searchQuery.substring(1)))
      ) {
        paramsRequests.caseID = searchQuery;
      } else if (searchQuery) {
        paramsRequests.title = searchQuery;
        paramsMediators.fullName = searchQuery;
      }
    }

    if (activeTab === "roles") {
      const [response] = await getRoles(undefined, paramsRequests);
      setRoles(response.data?.roles || []);
      setTotalPages(Math.ceil(response.data?.totalPages));
    } else if (activeTab === "privileges") {
      const [response] = await getPrivileges(undefined, paramsMediators);
      setPrivileges(response?.data?.privileges || []);
      setTotalPages(Math.ceil(response.data?.totalPages));
    }
  };

  function handlePageChange(page: number) {
    setCurrentPage(page);
  }

  const handleSearchChange = (event: any) => {
    setSearchQuery(event.target.value);
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(event.target.value);
  };

  const createPrivilege = () => {
    setModalVisible(true);
  };
  const createRole = () => {
    setRoleModalVisible(true);
  };
  const openNotification = () => {
    setModalNotifyVisible(true);
  };

  return (
    <>
      <div className="flex justify-between w-[95%]">
        <section>
          <h2 className="text-[24px] font-bold">
            Roles and Privileges
            <p className="text-[14px] font-normal">
              See all roles and their privileges
            </p>
          </h2>
        </section>

        <section className="flex gap-4">
          <div className="border-2 rounded-md solid pl-5 bg-transparent h-[45px] flex gap-3">
            <Icon name="searchIcon" className="mt-3 rounded" />
            <input
              className="outline-none border-none w-[80%] bg-transparent"
              id="input-placeholder"
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          <Icon name="downloadIcon" />
          <button className="-mt-3" onClick={openNotification}>
            <Icon name="notificationIcon" />
          </button>
        </section>
      </div>

      <div className="flex justify-between pr-10">
        <Tabs
          activeTab={activeTab}
          tabs={["roles", "privileges"]}
          setActiveTab={setActiveTab}
        />

        {activeTab === "roles" && (
          <button
            className="h-[50px] mt-8 w-[200px]  border-2 border-[#0979A1] text-[#0979A1] rounded-md font-bold text-[12px] "
            onClick={createRole}
          >
            Create A New Role
          </button>
        )}

        {activeTab === "privileges" && (
          <button
            className="h-[50px] mt-8 w-[200px]  border-2 border-[#0979A1] text-[#0979A1] rounded-md font-bold text-[12px] "
            onClick={createPrivilege}
          >
            Create A New Privilege
          </button>
        )}
      </div>

      {activeTab === "roles" && (
        <RolesTable
          data={roles}
          selectedStatus={selectedStatus}
          handleStatusChange={handleStatusChange}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      )}

      {activeTab === "privileges" && <PrivilegesTable data={privileges} />}

      <CreateRole
        visible={roleModalVisible}
        handleClose={() => setRoleModalVisible(false)}
      />

      <CreatePrivilege
        visible={modalVisible}
        handleClose={() => setModalVisible(false)}
      />

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      <NotificationModal
        visible={modalNotifyVisible}
        handleClose={() => setModalNotifyVisible(false)}
      />
    </>
  );
};

export default Roles;
